import { fetchRequestWrapper } from "@/lib/utilities";
import { componentsList } from "@/lib/widgetComponentsDir";
import {
  IActiveTabData,
  IbaseWidgetDetails,
  IFullWorkSpaceTab,
  IState,
  IWidgetDetails,
  IWorkspaceTabs,
} from "@/lib/widgetsLayoutDir";
import { Layout, Layouts } from "react-grid-layout";
import workspacesTabsStore, { State } from "./workspacesTabsStore";

const defaultTabWhenEmptyOrNew = {
  stateJson:
    '{"currentBreakpoint":"lg","compactType":"vertical","mounted":false,"layouts":{"lg":[],"sm":[]},"cols":{"lg":12,"sm":4},"openWidgets":[]}',
  active: true,
  displayName: "New",
  tabId: 0, //Assigned a number here. The return data from the endpoint will overwrite this id and send back the allocated ID.
};
// updateWorkspaceTabActionAdd - Add a widget to the active tab.
export function updateWorkspaceTabActionAdd(
  incomingData: IbaseWidgetDetails,
  currentTabStateJSON: IState,
) {
  const prevState = currentTabStateJSON;

  const newData = {
    ...incomingData,
    i: `${incomingData.displayName}-${crypto.randomUUID()}`,
    x:
      (currentTabStateJSON.layouts[currentTabStateJSON.currentBreakpoint]
        .length *
        incomingData.w) %
      (currentTabStateJSON.cols[currentTabStateJSON.currentBreakpoint] || 12),

    y: currentTabStateJSON.layouts[
      currentTabStateJSON.currentBreakpoint
    ].reduce(
      (max: number, layoutArr) => Math.max(max, layoutArr.y + layoutArr.h),
      0,
    ),
  };

  delete newData.icon; // Prevents the "Icon" getting saved to the dataBase.

  const tempOpenWidgets = currentTabStateJSON.openWidgets.slice(0);
  tempOpenWidgets.push(newData);

  const newTabData = {
    ...prevState,
    layouts: {
      // ...prevState.layouts,
      lg: [...prevState.layouts.lg, newData],
      sm: [...prevState.layouts.sm, newData],
    },
    openWidgets: tempOpenWidgets,
  };

  const idOfActiveTab = workspacesTabsStore
    .getState()
    .tabNamesAndActive.find((tab) => tab.active)!.tabId;

  workspacesTabsStore.getState().updateWholeTab(newTabData, idOfActiveTab);
}
// updateWorkspaceTabActionDelete - Delete a widget from the active tab.
export function updateWorkspaceTabActionDelete(
  incomingData: Layout,
  currentTabStateJSON: IState,
) {
  const prevState = currentTabStateJSON;

  const newTabData = {
    ...prevState,
    layouts: {
      ...prevState.layouts,
      lg: prevState.layouts.lg.filter((x) => x.i !== incomingData.i),
      sm: prevState.layouts.sm.filter((x) => x.i !== incomingData.i),
    },
    openWidgets: prevState.openWidgets.filter(
      (x: IWidgetDetails) => x.i !== incomingData.i,
    ),
  };
  const idOfActiveTab = workspacesTabsStore
    .getState()
    .tabNamesAndActive.find((tab) => tab.active)?.tabId;

  if (idOfActiveTab != undefined) {
    workspacesTabsStore.getState().updateWholeTab(newTabData, idOfActiveTab);
  }
}
// LQC-1837: read the live tab state straight from the store. Kept local rather than
// importing returnCurrentTabStateJSON from widgetHelperFunctions, which would close an
// import cycle back into this file.
function returnStoredStateJsonString(tabId: number): string | undefined {
  const stateJson = workspacesTabsStore
    .getState()
    .workspacesTabs.find((tab) => tab.tabId == tabId)?.stateJson;

  if (!stateJson) return undefined;
  return typeof stateJson === "string" ? stateJson : JSON.stringify(stateJson);
}

function returnStoredTabState(tabId: number): IState | undefined {
  const stateJson = returnStoredStateJsonString(tabId);
  return stateJson == undefined ? undefined : JSON.parse(stateJson);
}

// updateWorkspaceTabActionUpdate - Update the stateJson for the active tab (used for things like drag and drop)
export function updateWorkspaceTabActionUpdate(
  incomingData: Layouts | IWidgetDetails[],
  currentTabStateJSON: IState,
  getAccessTokenSilently: Function,
  isOpenWidgetsData = false,
) {
  const idOfActiveTab = workspacesTabsStore
    .getState()
    .tabNamesAndActive.find((tab) => tab.active)?.tabId;

  if (idOfActiveTab == undefined) return;

  // LQC-1837: merge onto the CURRENT store state, not the caller's snapshot. Callers capture
  // currentTabStateJSON before other writes land, so spreading it reverted whichever field
  // the other writer had just changed (layouts vs openWidgets clobbering each other).
  const prevState = returnStoredTabState(idOfActiveTab) ?? currentTabStateJSON;

  const newTabData: IState = isOpenWidgetsData
    ? { ...prevState, openWidgets: incomingData as IWidgetDetails[] }
    : { ...prevState, layouts: incomingData as Layouts };

  // Store first, persist second. The PUT no longer writes back to the store, so a slow
  // response can't resurrect an older snapshot and restart the render/save loop.
  workspacesTabsStore.getState().updateWholeTab(newTabData, idOfActiveTab);
  requestTabSave(idOfActiveTab, getAccessTokenSilently);
}

interface IWorkspace {
  tabs: IFullWorkSpaceTab[];
  widgets: string[];
}

// initialWorkspaceTabsFromAPI - Use a 'new tab' template when a user logs in for the first time.
export async function initialWorkspaceTabsFromAPI(
  getAccessTokenSilently: Function,
  isStrictModeUseEffectFirstMount: boolean,
) {
  let workspace: IWorkspace = await fetchRequestWrapper(
    { url: "/UserSettings/Workspace" },
    getAccessTokenSilently,
  );
  let newTabCreated = null;

  if (workspace.tabs.length == 0) {
    if (isStrictModeUseEffectFirstMount) {
      newTabCreated = await fetchRequestWrapper(
        {
          url: "/UserSettings/Workspace/Tab",
          method: "POST",
          objToSend: defaultTabWhenEmptyOrNew,
        },
        getAccessTokenSilently,
      );

      workspace.tabs.push(newTabCreated);
    } else {
      // OBJ needs to be in store for first load. Used due to useEffect running 2 times in DEV due to strictMode.
      workspace.tabs.push(defaultTabWhenEmptyOrNew);
    }
  } else {
    // Moved logic to cleanse data into an else block so it is not run when a user just logs in for the first time.
    // Workspace comes back from the database and will attempt to be read by code such as RGL
    // Need to have a check here to remove any values that will not be able to be read from code down the line.
    const arrayOfComponentsThatCanBeLoaded = Object.keys(componentsList);

    const workspacesAsAnObject = workspace.tabs.map((tab: IFullWorkSpaceTab) => {
      const data: IFullWorkSpaceTab = {
        tabId: tab.tabId,
        displayName: tab.displayName,
        active: tab.active,
        stateJson: JSON.parse(tab.stateJson),
      };
      return data;
    });

    const iValuesToRemove: string[] = []; // An array of i values that will cause the app to crash are captuerd in this arr.

    workspacesAsAnObject.forEach((tab: IFullWorkSpaceTab) => {
      if (tab.stateJson.openWidgets) {
        tab.stateJson.openWidgets.forEach((widget: IWidgetDetails) => {
          if (!arrayOfComponentsThatCanBeLoaded.includes(widget.component)) {
            iValuesToRemove.push(widget.i);
          }
          // workspace.widgets holds the string array of widgets that the user CAN see.
          else if (!workspace.widgets.includes(widget.component)) {
            iValuesToRemove.push(widget.i);
          }
        });
      }
    });

    if (iValuesToRemove.length > 0) {
      // only run the code to cleanse the workspace data if there are i values that will cause an issue.
      const cleansedWorkspaceTabs = workspacesAsAnObject.map(
        (tab: IFullWorkSpaceTab) => {
          return {
            ...tab, // Must remove the bad values from 3 locations
            stateJson: {
              ...tab.stateJson,
              openWidgets: tab.stateJson.openWidgets.filter(
                // Location #1 the open widgets.
                (widget: IWidgetDetails) => !iValuesToRemove.includes(widget.i),
              ),
              layouts: {
                ...tab.stateJson.layouts,
                lg: tab.stateJson.layouts.lg.filter(
                  // Location #2 the lg layout.
                  (widget: IWidgetDetails) =>
                    !iValuesToRemove.includes(widget.i),
                ),
                sm: tab.stateJson.layouts.sm.filter(
                  // Location #3 the sm layout.
                  (widget: IWidgetDetails) =>
                    !iValuesToRemove.includes(widget.i),
                ),
              },
            },
          };
        },
      );

      const cubeDefaultWorkspaceTabArr = cleansedWorkspaceTabs.map(
        (tab: IFullWorkSpaceTab) => {
          // the workspaceTabs stores are expecting string values in the stateJson - Handle here.
          return {
            ...tab,
            stateJson: JSON.stringify(tab.stateJson),
          };
        },
      );

      const newWorkspacesObject = {
        tabs: cubeDefaultWorkspaceTabArr,
        widgets: workspace.widgets,
      };
      workspace = newWorkspacesObject; // the object that usually does NOT need cleansing will be replaced with the cleansed object if required.
    } // current code that accepts the incoming database object will now use the cleansed object.
  }

  const dataForActiveTabs = workspace.tabs.map((tab: IFullWorkSpaceTab) => {
    const data: IActiveTabData = {
      tabId: tab.tabId,
      displayName: tab.displayName,
      active: tab.active,
    };
    return data;
  });

  const dataForWorkspaces = workspace.tabs.map((tab: IFullWorkSpaceTab) => {
    const data: IWorkspaceTabs = {
      tabId: tab.tabId,
      stateJson: tab.stateJson,
    };
    return data;
  });

  workspacesTabsStore.getState().setWorkspacesTabs(dataForWorkspaces);
  workspacesTabsStore.getState().setTabNamesAndActive(dataForActiveTabs);
  workspacesTabsStore.getState().setWidgetsAvailable(workspace.widgets); // List for widgets to choose from.
}

// break up the data into a smaller tabs only object that can be used for the store to prevent rendering on all UI changes
// updateTabDataAction(dataForActiveTabs, getAccessTokenSilently);
// }

// updateTabDataAction - Used in clicking between tabs to change the 'active' tab, or double clicking to cahnge the display name of a tab.
export function updateTabDataAction(
  incomingData: State["tabNamesAndActive"],
  getAccessTokenSilently: Function,
) {
  workspacesTabsStore.getState().setTabNamesAndActive(incomingData);

  // in order to send the change in active tabs back to the database, we are going to merge the data back together from the two store values.
  const combinedArray: IFullWorkSpaceTab[] = workspacesTabsStore
    .getState()
    .workspacesTabs.map((tab1) => {
      const tab2 = workspacesTabsStore
        .getState()
        .tabNamesAndActive.find((tab2) => tab2.tabId === tab1.tabId);

      const newTab: IFullWorkSpaceTab = {
        tabId: tab2!.tabId,
        active: tab2!.active,
        displayName: tab2!.displayName,
        stateJson: tab1.stateJson,
      };
      return newTab;
    });

  updateBackendAllTabsChanged(combinedArray, getAccessTokenSilently);
}

// Function to create a newTab
export async function addNewWorkspaceTabToUser(
  getAccessTokenSilently: Function,
) {
  const currentWorkspaceList = [
    ...workspacesTabsStore.getState().workspacesTabs,
  ];
  const currentTabsList = workspacesTabsStore
    .getState()
    .tabNamesAndActive.map((tab) => ({
      tabId: tab.tabId,
      active: false,
      displayName: tab.displayName,
    }));

  const newTabCreated = await fetchRequestWrapper(
    {
      url: "/UserSettings/Workspace/Tab",
      method: "POST",
      objToSend: defaultTabWhenEmptyOrNew,
    },
    getAccessTokenSilently,
  );

  currentWorkspaceList.push({
    tabId: newTabCreated.tabId,
    stateJson: newTabCreated.stateJson,
  });

  currentTabsList.push({
    tabId: newTabCreated.tabId,
    active: newTabCreated.active,
    displayName: newTabCreated.displayName,
  });

  workspacesTabsStore.getState().setWorkspacesTabs(currentWorkspaceList);
  workspacesTabsStore.getState().setTabNamesAndActive(currentTabsList);

  // in order to send the change in active tabs back to the database, we are going to merge the data back together from the two store values.
  const combinedArray: IFullWorkSpaceTab[] = currentWorkspaceList.map(
    (tab1) => {
      const tab2 = currentTabsList.find((tab2) => tab2.tabId === tab1.tabId);

      const newTab: IFullWorkSpaceTab = {
        tabId: tab2!.tabId,
        active: tab2!.active,
        displayName: tab2!.displayName,
        stateJson: tab1.stateJson,
      };
      return newTab;
    },
  );

  updateBackendAllTabsChanged(combinedArray, getAccessTokenSilently);
}

export async function deleteWorkspaceTabFromUser(
  getAccessTokenSilently: Function,
  tabID: number,
) {
  const currentTabsList = [...workspacesTabsStore.getState().tabNamesAndActive];
  const currentWorkspaceList = [
    ...workspacesTabsStore.getState().workspacesTabs,
  ];

  const currentActiveIndex = currentTabsList.findIndex((tab) => tab.active);

  if (currentTabsList[currentActiveIndex].tabId == tabID) {
    if (currentActiveIndex < currentTabsList.length - 1) {
      currentTabsList[currentActiveIndex + 1].active = true;
    } else {
      currentTabsList[currentActiveIndex - 1].active = true;
    }
  }
  const filteredTabsList = currentTabsList.filter((tab) => tab.tabId != tabID);

  const filteredWorkspaceTabse = currentWorkspaceList.filter(
    (tab) => tab.tabId != tabID,
  );
  // Filter workspace store and delete passed in tabID
  workspacesTabsStore.getState().setWorkspacesTabs(filteredWorkspaceTabse);
  workspacesTabsStore.getState().setTabNamesAndActive(filteredTabsList);

  await fetchRequestWrapper(
    {
      url: `/UserSettings/Workspace/Tab/${tabID}`,
      method: "DELETE",
      ignoreResponse: true,
    },
    getAccessTokenSilently,
  );

  // in order to send the change in active tabs back to the database, we are going to merge the data back together from the two store values.
  const combinedArray: IFullWorkSpaceTab[] = filteredWorkspaceTabse.map(
    (tab1) => {
      const tab2 = filteredTabsList.find((tab2) => tab2.tabId === tab1.tabId);

      const newTab: IFullWorkSpaceTab = {
        tabId: tab2!.tabId,
        active: tab2!.active,
        displayName: tab2!.displayName,
        stateJson: tab1.stateJson,
      };
      return newTab;
    },
  );
  updateBackendAllTabsChanged(combinedArray, getAccessTokenSilently);
}

// LQC-1837: debounced per tab, so a burst of actions collapses into one PUT instead of
// several racing ones. The payload is read from the store when the timer fires, never from a
// caller's snapshot, so a save can't send stale data.
const TAB_SAVE_DEBOUNCE_MS = 400;
const tabSaveTimers = new Map<number, ReturnType<typeof setTimeout>>();

export function requestTabSave(
  tabId: number,
  getAccessTokenSilently: Function,
) {
  clearTimeout(tabSaveTimers.get(tabId));

  tabSaveTimers.set(
    tabId,
    setTimeout(() => {
      tabSaveTimers.delete(tabId);
      saveTabToDB(tabId, getAccessTokenSilently);
    }, TAB_SAVE_DEBOUNCE_MS),
  );
}

// Database call used to update the stateJson of one particular tab. This will be saved to the layout of react Grid Layout.
async function saveTabToDB(tabId: number, getAccessTokenSilently: Function) {
  const stateJson = returnStoredStateJsonString(tabId);
  if (stateJson == undefined) return;

  try {
    await fetchRequestWrapper(
      {
        url: "/UserSettings/Workspace/Tab",
        method: "PUT",
        objToSend: { tabId, stateJson },
        ignoreResponse: true,
      },
      getAccessTokenSilently,
    );
  } catch {
    // fetchRequestWrapper already toasts and logs. Caught so a failed save isn't an unhandled
    // rejection - the store keeps the change and the next action saves it again.
  }
}

async function updateBackendAllTabsChanged(
  workspacesTabs: IFullWorkSpaceTab[],
  getAccessTokenSilently: Function,
) {
  const workspace = { tabs: workspacesTabs };

  await fetchRequestWrapper(
    {
      url: "/UserSettings/Workspace",
      method: "PUT",
      objToSend: workspace,
      ignoreResponse: true,
    },
    getAccessTokenSilently,
  );
}
